import React, { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover } from "@mui/material";

import { IModule } from "../../types";
import { MODULE_META } from "../../constants";
import { IconApps, IconPinFilled, IconPinOutline } from "../../icons";

interface AppSwitcherProps {
  erp_modules: IModule[];
  selectedModule: IModule | null;
}

export function AppSwitcher({ erp_modules, selectedModule }: AppSwitcherProps) {
  const navigate = useNavigate();
  const appSwitchRef = useRef<HTMLButtonElement>(null);
  const [appSwitchOpen, setAppSwitchOpen] = useState(false);
  const [asSrch, setAsSrch] = useState("");
  const [pinnedModules, setPinnedModules] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("_pinned_modules") || "[]"); }
    catch { return []; }
  });

  const togglePin = useCallback((link: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedModules(prev => {
      const next = prev.includes(link) ? prev.filter(l => l !== link) : [...prev, link];
      localStorage.setItem("_pinned_modules", JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredModules = useMemo(() => {
    if (!asSrch.trim()) return erp_modules;
    return erp_modules.filter(m =>
      String(m.label).toLowerCase().includes(asSrch.toLowerCase())
    );
  }, [erp_modules, asSrch]);

  const pinnedList = useMemo(() =>
    erp_modules.filter(m => pinnedModules.includes(m.link)),
    [erp_modules, pinnedModules]
  );

  return (
    <>
      <button
        ref={appSwitchRef}
        className={`app-switch${appSwitchOpen ? " open" : ""}`}
        type="button"
        aria-label="Switch module"
        aria-haspopup="true"
        aria-expanded={appSwitchOpen}
        onClick={() => setAppSwitchOpen(v => !v)}
      >
        <IconApps />
      </button>

      <Popover
        open={appSwitchOpen}
        anchorEl={appSwitchRef.current}
        onClose={() => { setAppSwitchOpen(false); setAsSrch(""); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { className: "as-pop", elevation: 0 } }}
        marginThreshold={8}
        disableScrollLock
      >
        <div className="as-head">
          <input
            className="as-search-input"
            placeholder="Search modules, apps, recent…"
            value={asSrch}
            onChange={(e) => setAsSrch(e.target.value)}
            autoFocus
          />
          <span className="as-pin">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 11, height: 11 }}>
              <path d="M12 2v10"/><path d="M5 12l7 7 7-7"/>
            </svg>
            Pin to start
          </span>
        </div>

        <div className="as-body">
          {pinnedList.length > 0 && (
            <div className="as-section">
              <div className="as-section-label">
                Pinned <span className="badge">{pinnedList.length}</span>
              </div>
              <div className="as-grid">
                {pinnedList.map((m, i) => (
                  <button
                    key={i}
                    className="as-tile pinned"
                    type="button"
                    style={{ "--tile-bg": m.bg || "#F2F3F4", "--tile-fg": MODULE_META[m.link]?.fg || "#4A4C50" } as React.CSSProperties}
                    onClick={() => { setAppSwitchOpen(false); navigate(m.link); setAsSrch(""); }}
                  >
                    <span className="as-tile-icon">
                      <img src={m.icon} alt="" width={15} height={15} />
                    </span>
                    <div>
                      <div className="as-tile-row">
                        <div className="as-tile-name">{m.label}</div>
                        {MODULE_META[m.link]?.isNew && <span className="new-pill">NEW</span>}
                      </div>
                      {MODULE_META[m.link]?.meta && (
                        <div className="as-tile-meta">{MODULE_META[m.link].meta}</div>
                      )}
                    </div>
                    <span className="pin" onClick={(e) => togglePin(m.link, e)} title="Unpin">
                      <IconPinFilled />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="as-section">
            {pinnedList.length > 0 && (
              <div className="as-section-label">
                All Apps <span className="badge">{filteredModules.length}</span>
              </div>
            )}
            <div className="as-grid">
              {filteredModules.map((m, i) => {
                const isPinned = pinnedModules.includes(m.link);
                const isActive = selectedModule?.link === m.link;
                return (
                  <button
                    key={i}
                    className={`as-tile${isPinned ? " pinned" : ""}${isActive ? " active" : ""}`}
                    type="button"
                    style={{ "--tile-bg": m.bg || "#F2F3F4", "--tile-fg": MODULE_META[m.link]?.fg || "#4A4C50" } as React.CSSProperties}
                    onClick={() => { setAppSwitchOpen(false); navigate(m.link); setAsSrch(""); }}
                  >
                    <span className="as-tile-icon">
                      <img src={m.icon} alt="" width={15} height={15} />
                    </span>
                    <div>
                      <div className="as-tile-row">
                        <div className="as-tile-name">{m.label}</div>
                        {MODULE_META[m.link]?.isNew && <span className="new-pill">NEW</span>}
                      </div>
                      {MODULE_META[m.link]?.meta && (
                        <div className="as-tile-meta">{MODULE_META[m.link].meta}</div>
                      )}
                    </div>
                    <span
                      className="pin"
                      onClick={(e) => togglePin(m.link, e)}
                      title={isPinned ? "Unpin" : "Pin"}
                    >
                      {isPinned ? <IconPinFilled /> : <IconPinOutline />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="as-foot">
          <span>⌘ + 1–9 to switch modules instantly</span>
          <span className="right">
            <a onClick={() => setAppSwitchOpen(false)}>Customize workspace →</a>
          </span>
        </div>
      </Popover>
    </>
  );
}

export default AppSwitcher;
